import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingPage } from '../components/ui/Loading';
import { isAdminEquivalent } from '../utils/permissions';

// Layout components
import Layout from '../components/Layout';

// Pages
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';
import Dashboard from '../pages/Dashboard';
import Characters from '../pages/Characters';
import CharacterDetail from '../pages/CharacterDetail';
import Events from '../pages/Events';
import EventDetail from '../pages/EventDetail';
import Ranking from '../pages/Ranking';
import RecentActivities from '../pages/RecentActivities';
import Profile from '../pages/Profile';
import Blog from '../pages/Blog';
import BlogPostDetail from '../pages/BlogPostDetail';

// Admin pages
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminEnlistments from '../pages/admin/AdminEnlistments';
import AdminUsers from '../pages/admin/AdminUsers';
import AdminItems from '../pages/admin/AdminItems';
import AdminRelics from '../pages/admin/AdminRelics';
import AdminQuests from '../pages/admin/AdminQuests';
import AdminBlog from '../pages/admin/AdminBlog';
import AdminBlogForm from '../pages/admin/AdminBlogForm';
import AdminBlogCategories from '../pages/admin/AdminBlogCategories';
import AdminProofs from '../pages/admin/AdminProofs';
import AdminNotifications from '../pages/admin/AdminNotifications';
import AdminDeclinedEvents from '../pages/admin/AdminDeclinedEvents';
import AdminRanking from '../pages/admin/AdminRanking';
import AdminReports from '../pages/admin/AdminReports';
import AdminEventReport from '../pages/admin/AdminEventReport';
import AdminBcParticipationReport from '../pages/admin/AdminBcParticipationReport';
import AdminClanRotationLogReport from '../pages/admin/AdminClanRotationLogReport';
import AdminAccessLogReport from '../pages/admin/AdminAccessLogReport';
import SpecialEventsList from '../pages/admin/SpecialEventsList';
import SpecialEventForm from '../pages/admin/SpecialEventForm';
import SpecialEventManager from '../pages/admin/SpecialEventManager';
import DreamTeamBuilder from '../pages/admin/DreamTeamBuilder';
import BcPaymentPage from '../pages/admin/BcPaymentPage';
import ClubTreasuryPage from '../pages/admin/ClubTreasuryPage';
import BcPaymentLookupPage from '../pages/admin/BcPaymentLookupPage';

// Recap pages
import RecapSelection from '../pages/RecapSelection';
import CharacterRecap from '../pages/CharacterRecap';

// 404 Page
import NotFound from '../pages/NotFound';
import TransparencyPage from '../pages/TransparencyPage';

// Protected Route component
const ProtectedRoute = ({ children, adminOnly = false, allowedRoles = [] }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/app/login" replace state={{ from: location }} />;
  }

  // Se adminOnly está definido, verifica apenas se é ADMIN
  if (adminOnly && !isAdminEquivalent(user?.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  // Se allowedRoles está definido, verifica se o role do usuário está na lista
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

// Public Route component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />

      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPassword />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/characters" element={
        <ProtectedRoute>
          <Layout>
            <Characters />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/characters/:id" element={
        <ProtectedRoute>
          <Layout>
            <CharacterDetail />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/events" element={
        <ProtectedRoute>
          <Layout>
            <Events />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/events/:id" element={
        <ProtectedRoute>
          <Layout>
            <EventDetail />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/ranking" element={
        <ProtectedRoute>
          <Layout>
            <Ranking />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/activities" element={
        <ProtectedRoute>
          <Layout>
            <RecentActivities />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/transparency" element={
        <ProtectedRoute>
          <Layout>
            <TransparencyPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/blog" element={
        <ProtectedRoute>
          <Layout>
            <Blog />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/blog/:id" element={
        <ProtectedRoute>
          <Layout>
            <BlogPostDetail />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/enlistments" element={
        <ProtectedRoute allowedRoles={['ADMIN', 'RECRUITER', 'TESOUREIRO', 'SUPER_ADMIN']}>
          <Layout>
            <AdminEnlistments />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminUsers />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/items" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminItems />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/relics" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminRelics />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/quests" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminQuests />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/blog" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminBlog />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/blog/new" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminBlogForm />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/blog/categories" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminBlogCategories />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/blog/:id/edit" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminBlogForm />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/notifications" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminNotifications />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/proof-review" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminProofs />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/declined-events" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminDeclinedEvents />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/ranking" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminRanking />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/reports" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminReports />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/reports/event-participation" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminEventReport />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/reports/bc-participation" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminBcParticipationReport />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/reports/clan-rotation-log" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminClanRotationLogReport />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/reports/access-log" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <AdminAccessLogReport />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Recap Routes */}
      <Route path="/recap" element={
        <ProtectedRoute>
          <RecapSelection />
        </ProtectedRoute>
      } />

      <Route path="/recap/:characterId" element={
        <ProtectedRoute>
          <CharacterRecap />
        </ProtectedRoute>
      } />

      {/* Special Events Routes */}
      <Route path="/admin/special-events" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <SpecialEventsList />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/special-events/new" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <SpecialEventForm />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/special-events/dream-team" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <DreamTeamBuilder />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/special-events/:id/edit" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <SpecialEventForm />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/special-events/:id" element={
        <ProtectedRoute adminOnly>
          <Layout>
            <SpecialEventManager />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/special-events/:id/bc-payment" element={
        <ProtectedRoute allowedRoles={['TESOUREIRO', 'SUPER_ADMIN']}>
          <Layout>
            <BcPaymentPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/treasury" element={
        <ProtectedRoute allowedRoles={['TESOUREIRO', 'SUPER_ADMIN']}>
          <Layout>
            <ClubTreasuryPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/bc-payments/lookup" element={
        <ProtectedRoute allowedRoles={['TESOUREIRO', 'SUPER_ADMIN']}>
          <Layout>
            <BcPaymentLookupPage />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Redirect /app to /app/dashboard if authenticated, otherwise to /app/login */}
      <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
      
      {/* 404 - Catch all unmatched routes within /app/* */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default AppRoutes;
